import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Card, CircularProgress, Option, Select, Sheet, Stack, Table, Typography,
} from '@mui/joy';

import { usePartner } from '../../PartnerContext';
import {
    APIPartnerAdRevenueSummary, APIPartnerAdRevenueTimeline, APIPartnerAdRevenueBreakdown,
} from '../../api_partner';
import { formatMoney } from '../Ads/adsConstants';
import { rangeFor, PERIOD_OPTIONS } from '../partnerPeriod';

const SCOPE_LABELS = { city: 'Город', region: 'Регион', all_russia: 'Вся Россия' };

function Metric({ label, value }) {
    return (
        <Card variant="soft" sx={{ flex: 1, minWidth: 150, p: 1.5 }}>
            <Typography level="body-xs" color="neutral">{label}</Typography>
            <Typography level="h4">{value}</Typography>
        </Card>
    );
}

export default function PartnerAdRevenue() {
    const { partnerToken: token } = usePartner();
    const [days, setDays] = useState(30);
    const [summary, setSummary] = useState(null);
    const [timeline, setTimeline] = useState([]);
    const [byCity, setByCity] = useState([]);
    const [byScope, setByScope] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        setError(null);
        try {
            const opts = rangeFor(days);
            const [s, t, b] = await Promise.all([
                APIPartnerAdRevenueSummary(token, opts),
                APIPartnerAdRevenueTimeline(token, opts),
                APIPartnerAdRevenueBreakdown(token, opts),
            ]);
            setSummary(s);
            setTimeline(t?.items || []);
            setByCity(b?.by_city || []);
            setByScope(b?.by_scope || []);
        } catch (e) {
            setError('Не удалось загрузить доход с рекламы');
        } finally {
            setLoading(false);
        }
    }, [token, days]);

    useEffect(() => { load(); }, [load]);

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography level="title-md">Доход с рекламы на территории</Typography>
                <Select size="sm" value={days} onChange={(_, v) => setDays(v)} sx={{ minWidth: 130 }}>
                    {PERIOD_OPTIONS.map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                </Select>
            </Stack>

            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}
            {loading ? (
                <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
            ) : (
                <>
                    {summary && (
                        <>
                            <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mb: 1 }}>
                                <Metric label="Заработано за период" value={`${formatMoney(summary.earned)} ₽`} />
                                <Metric label="Расход рекламодателей" value={`${formatMoney(summary.gross_spend)} ₽`} />
                                <Metric label="Всего заработано" value={`${formatMoney(summary.earned_lifetime)} ₽`} />
                            </Stack>
                            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                                Применяемый процент:{' '}
                                {summary.ad_commission_percent != null
                                    ? `${summary.ad_commission_percent}% (индивидуальный)`
                                    : 'по типу охвата из настроек рекламы'}.
                            </Typography>
                        </>
                    )}

                    {byScope.length > 0 && (
                        <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto', mb: 2 }}>
                            <Typography level="title-sm" sx={{ p: 1.5, pb: 0.5 }}>Расход по типу охвата</Typography>
                            <Table size="sm" sx={{ '--TableCell-paddingY': '8px' }}>
                                <thead><tr><th>Охват</th><th>Расход рекламодателей, ₽</th></tr></thead>
                                <tbody>
                                    {byScope.map((r, i) => (
                                        <tr key={i}><td>{SCOPE_LABELS[r.scope] || r.scope}</td><td>{formatMoney(r.spend)}</td></tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>
                    )}

                    {byCity.length > 0 && (
                        <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto', mb: 2 }}>
                            <Typography level="title-sm" sx={{ p: 1.5, pb: 0.5 }}>Расход по городам</Typography>
                            <Table size="sm" sx={{ '--TableCell-paddingY': '8px' }}>
                                <thead><tr><th>Город</th><th>Расход рекламодателей, ₽</th></tr></thead>
                                <tbody>
                                    {byCity.map((r, i) => (
                                        <tr key={i}><td>{r.city}</td><td>{formatMoney(r.spend)}</td></tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>
                    )}

                    {timeline.length > 0 && (
                        <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                            <Typography level="title-sm" sx={{ p: 1.5, pb: 0.5 }}>По дням</Typography>
                            <Table size="sm" sx={{ '--TableCell-paddingY': '8px' }}>
                                <thead><tr><th>Дата</th><th>Расход, ₽</th><th>%</th><th>Начислено, ₽</th></tr></thead>
                                <tbody>
                                    {timeline.map((r, i) => (
                                        <tr key={i}>
                                            <td>{r.date}</td><td>{formatMoney(r.gross_spend)}</td>
                                            <td>{r.percent}%</td><td>{formatMoney(r.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>
                    )}
                </>
            )}
        </Box>
    );
}